'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { type Language, getTranslations, loadLanguagePreference, saveLanguagePreference } from '@/lib/i18n';
import type en from '@/locales/en.json';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof en;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');

  // On mount, load from localStorage or detect from browser
  useEffect(() => {
    const preferred = loadLanguagePreference();
    setLanguageState(preferred);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    saveLanguagePreference(lang);
  }, []);

  const t = getTranslations(language);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
